import { OverlayModule } from '@angular/cdk/overlay';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule, isDevMode } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import * as Hammer from 'hammerjs';
import { ClickOutsideModule } from 'src/app/shared/directive/click-outside/click-outside.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppDownloadTipsComponent } from './layouts/app-download-tips/app-download-tips.component';
import { BasicLayoutComponent } from './layouts/basic-layout/basic-layout.component';
import { BottomMenuComponent } from './layouts/bottom-menu/bottom-menu.component';
import { CurrencyWalletModule } from './layouts/currency-wallet/currency-wallet.module';
import { FooterModule } from './layouts/footer/footer.module';
import { GuideComponent } from './layouts/guide/guide.component';
import { HeaderComponent } from './layouts/header/header.component';
import { LeftMenuComponent } from './layouts/left-menu/left-menu.component';
import { MemberLayoutComponent } from './layouts/member-layout/member-layout.component';
import { StatisticsPanelComponent } from './layouts/statistics-panel/statistics-panel.component';
import { TopBarComponent } from './layouts/top-bar/top-bar.component';
import { LeftMenuActivityBarComponent } from './pages/activity/left-menu-activity-bar/left-menu-activity-bar.component';
import { LeaderBoardModule } from './pages/leader-board/leader-board.module';
import { MemberSettingComponent } from './pages/member-setting/member-setting.component';
import { MyComponent } from './pages/my/my.component';
import { PageNotFoundModule } from './pages/page-not-found/page-not-found.module';
import { DepositRedirectComponent } from './pages/top-up/deposit-redirect.component';
import { AuthPromptComponent } from './shared/components/auth-prompt/auth-prompt.component';
import { BindPopupComponent } from './shared/components/bind-popup/bind-popup.component';
import { BindVerifyPopupComponent } from './shared/components/bind-verify-popup/bind-verify-popup.component';
import { CodeInputModule } from './shared/components/code-input/code-input.module';
import { CustomizeFormModule } from './shared/components/customize-form/customize-form.module';
import { EarlyRechargePopupComponent } from './shared/components/early-recharge-popup/early-recharge-popup.component';
import { EmptyModule } from './shared/components/empty/empty.module';
import { Ganeral2faVerifyModule } from './shared/components/general2faverify/general2faverify.module';
import { LogoModule } from './shared/components/logo/logo.module';
import { PaginatorModule } from './shared/components/paginator/paginator.module';
import { PhoneNumberSelecterComponent } from './shared/components/phone-number-selecter/phone-number-selecter.component';
import { PreviewMediaComponent } from './shared/components/preview-media/preview-media.component';
import { SafetyNoticeDialogComponent } from './shared/components/safety-notice-dialog/safety-notice-dialog.component';
import { ScrollbarModule } from './shared/components/scrollbar/scrollbar.module';
import { SelectCurrencyModule } from './shared/components/select-currency/select-currency.module';
import { ThemeSwitchComponent } from './shared/components/theme-switch/theme-switch.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { ToolTipModule } from './shared/components/tool-tip/tool-tip.module';
import { IntersectionObserverModule } from './shared/directive/intersection-observer/intersection-observer.module';
import { LifeObserveModule } from './shared/directive/life-observe/life-observe.module';
import { LoadingModule } from './shared/directive/loading/loading.module';
import { MouseScrollModule } from './shared/directive/mouse-scroll/mouse-scroll.module';
import './shared/extensions/number-extensions';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { APP_BASE_HREF } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FingerprintJSPro, FingerprintjsProAngularModule } from '@fingerprintjs/fingerprintjs-pro-angular';
import { environment } from 'src/environments/environment';
import { CustomReuseStrategy } from './custom-reuse-strategy';
import { HeaderLevel2MenuComponent } from './layouts/header-level2-menu/header-level2-menu.component';
import { RightCombinePopupModule } from './layouts/right-aside/right-combine-popup/right-combine-popup.module';
import { ChatModule } from './pages/chat/chat.module';
import { ReferralIdModule } from './pages/friend/recommend/dialog/referral-id/referral-id.module';
import { CustomerServiceModule } from './shared/components/customer-service/customer-service.module';
import { DatepickerModule } from './shared/components/datepicker/datepicker.module';
import { StandardPopupModule } from './shared/components/standard-popup/standard-popup.module';
import { AvatarDefaultPipe } from './shared/pipes/avatar-default.pipe';
import { CountryFilterPipe } from './shared/pipes/cuontry-filter.pipe';
import { CurrencyValuePipe } from './shared/pipes/currency-value.pipe';
import { PipesModule } from './shared/pipes/pipes.module';

@Injectable()
class HammerConfig extends HammerGestureConfig {
  overrides = {
    pan: {},
    swipe: {
      direction: Hammer.DIRECTION_ALL,
    },
    press: { time: 1000 },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    PhoneNumberSelecterComponent,
    BasicLayoutComponent,
    // UserLayoutComponent,
    HeaderComponent,
    AuthPromptComponent,
    TopBarComponent,
    LeftMenuActivityBarComponent,
    LeftMenuComponent,
    BottomMenuComponent,
    ThemeSwitchComponent,
    AppDownloadTipsComponent,
    ToastComponent,
    PreviewMediaComponent,
    StatisticsPanelComponent,
    GuideComponent,
    BindPopupComponent,
    EarlyRechargePopupComponent,
    BindVerifyPopupComponent,
    MemberLayoutComponent,
    MyComponent,
    SafetyNoticeDialogComponent,
    MemberSettingComponent,
    HeaderLevel2MenuComponent,
    DepositRedirectComponent,
  ],
  imports: [
    CustomerServiceModule,
    LifeObserveModule,
    PageNotFoundModule,
    CustomizeFormModule,
    StandardPopupModule,
    LogoModule,
    OverlayModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    HammerModule,
    FormsModule,
    ChatModule,
    // QrCodeModule,
    MatDialogModule,
    MatCheckboxModule,
    CodeInputModule,
    MatSidenavModule,
    MatSlideToggleModule,
    PipesModule,
    // MatToolbarModule,
    IntersectionObserverModule,
    ClickOutsideModule,
    ScrollbarModule,
    LoadingModule,
    EmptyModule,
    DragDropModule,
    // MatBadgeModule,
    FooterModule,
    DatepickerModule,
    MouseScrollModule,
    SelectCurrencyModule,
    // MemberContentModule,
    // MemberFeedbackModule,
    CurrencyWalletModule,
    // RegisterModule,
    ToolTipModule,
    // LoginModule,
    LeaderBoardModule,
    // TopUpModule,
    MatTabsModule,
    // ExchangeModule,
    // WithdrawModule,
    MatTooltipModule,
    Ganeral2faVerifyModule,
    PaginatorModule,
    ReferralIdModule,
    RightCombinePopupModule,
    FingerprintjsProAngularModule.forRoot({
      loadOptions: {
        apiKey: environment.common.fpApiKey,
        endpoint: [environment.common.fpEndpoint],
        scriptUrlPattern: [
          `${environment.common.fpEndpointJS}/v<version>/<apiKey>/loader_v<loaderVersion>.js`,
          FingerprintJSPro.defaultScriptUrlPattern,
        ],
      },
    }),
    ServiceWorkerModule.register(`${window.location.origin}/ngsw-worker.js`, {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: window.location.origin },
    CountryFilterPipe,
    CurrencyValuePipe,
    AvatarDefaultPipe,
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseStrategy,
    },
  ],
  bootstrap: [AppComponent],
  // entryComponents: [],//TODO:新版会报错先隐藏 看起来没什么影响
})
export class AppModule {}
